<template>
  <div class="payment-method-list">
    <div
      v-if="showPaymentMethodListExistingItem"
      class="payment-method-list__wrapper"
    >
      <PaymentMethodListExistingItem
        @existing-payment-clicked="handleExistingPaymentMethod"
        @replace-payment-clicked="handleAddNewMethod"
      />
    </div>
    <div v-if="showPaymentMethodListItems" class="payment-method-list__wrapper">
      <PaymentMethodListItems
        @payment-method-selected="handlePaymentMethodSelected"
      />
    </div>
    <div v-if="showAddCreditCardForm" class="payment-method-list__wrapper">
      <AddCreditCardForm
        :isLoading="isLoading"
        :showTitle="true"
        @handle-recurly-token="handleNewMethodSuccess"
      />
    </div>
    <div v-if="showAddPaypal" class="payment-method-list__wrapper">
      <PaypalSession
        @creating-paypal-instance="handleCreatingPaypalInstance"
        @handle-recurly-token="handleNewMethodSuccess"
      />
    </div>
    <div v-if="showNewMethodWarning" class="payment-method-list__cta-wrapper">
      <PaymentMethodListWarning
        :showContinueBtn="!!paymentMethodOnFile && !showPaymentMethodListItems"
        @handle-continue="handleContinueNewPaymentMethod"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import PaymentMethodListExistingItem from '@/components/forms/payments/PaymentMethodListExistingItem.vue'
import PaymentMethodListWarning from '@/components/forms/payments/PaymentMethodListWarning.vue'
import PaymentMethodListItems from '@/components/forms/payments/PaymentMethodListItems.vue'
import AddCreditCardForm from '@/components/forms/payments/AddCreditCardForm.vue'
import PaypalSession from '@/components/forms/payments/PaypalSession.vue'
import { paymentCommons } from '@/helpers/mixins'
import CONSTANTS from '@/constants'

export default {
  name: 'PaymentMethodList',
  emits: [
    'existing-payment-clicked',
    'replace-payment-clicked',
    'payment-method-selected',
    'handle-recurly-token',
    'handle-continue',
    'update:show-add-credit-card-form',
  ],
  components: {
    PaymentMethodListWarning,
    PaymentMethodListExistingItem,
    AddCreditCardForm,
    PaymentMethodListItems,
    PaypalSession,
  },
  mixins: [paymentCommons],
  data() {
    return {
      displayNewMethodWarning: false,
      selectedMethodText: '',
      newPaymentMethod: false,
      newPaymentMethodSelectedCC: false,
      newPaymentMethodSelectedPaypal: false,
    }
  },
  computed: {
    ...mapState({
      paymentMethod: state => state.subscriptions.paymentMethod,
      paymentMethodOnFile: state => state.subscriptions.paymentInfo,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    showPaymentMethodListExistingItem() {
      return !this.newPaymentMethod && this.paymentMethodOnFile
    },
    showPaymentMethodListItems() {
      return (
        (this.newPaymentMethod ||
          this.newPaymentMethodSelectedCC ||
          this.newPaymentMethodSelectedPaypal) &&
        !this.showAddCreditCardForm
      )
    },
    showAddCreditCardForm() {
      return this.newPaymentMethodSelectedCC
    },
    showAddPaypal() {
      return this.newPaymentMethodSelectedPaypal
    },
    showNewMethodWarning() {
      return this.displayNewMethodWarning && !this.showAddCreditCardForm
    },
  },
  watch: {
    showAddCreditCardForm(newVal) {
      this.$emit('update:show-add-credit-card-form', newVal)
    },
  },
  mounted() {
    // Listen for the 'three-d-secure-validated' event
    this.$on('three-d-secure-validated', data =>
      this.handleThreeDSecureValidated(data)
    )
  },
  beforeDestroy() {
    // Remove the event listener when the component is destroyed
    this.$off('three-d-secure-validated', data =>
      this.handleThreeDSecureValidated(data)
    )
  },
  methods: {
    ...mapActions('subscriptions', ['getPaymentInfo']),
    handleExistingPaymentMethod() {
      this.$emit('existing-payment-clicked')
      this.displayNewMethodWarning = false
    },
    handleAddNewMethod() {
      this.$emit('replace-payment-clicked')
      this.displayNewMethodWarning = true
    },
    async handleNewMethodSuccess(token) {
      this.isLoading = true
      await this.handleRecurlyToken(token)
      // update payment info
      await this.getPaymentInfo()
      // go back to the payment method on file view
      this.newPaymentMethod = false
      this.newPaymentMethodSelectedCC = false
      this.newPaymentMethodSelectedPaypal = false
      this.isLoading = false
      this.handleCreatingPaypalInstance(false)
    },
    handleContinueNewPaymentMethod() {
      this.newPaymentMethod = true
    },
    handlePaymentMethodSelected(method) {
      this.newPaymentMethodSelectedCC =
        method === CONSTANTS.PAYMENT_METHODS.CREDIT_CARD
      this.newPaymentMethodSelectedPaypal =
        method === CONSTANTS.PAYMENT_METHODS.PAYPAL
    },
    async handleThreeDSecureValidated(data) {
      await this.getPaymentInfo()
    },
    handleCreatingPaypalInstance(status) {
      this.$emit('update:creating-paypal-instance', status)
    },
  },
}
</script>

<style lang="scss" scoped>
.payment-method-list {
  border-radius: 35px;
  width: 100%;
  max-width: 630px;
  display: flex;
  flex-flow: column;
  gap: 20px;
  @include mobile {
    border-radius: 10px;
    background: transparent;
    border-radius: unset;
    padding: 0;
    height: 100%;
  }
  &__title {
    color: var(--white);
    font-family: var(--font-regular);
    font-weight: 450;
    font-size: 24px;
    line-height: 32px;
    margin: 10px 0 15px 0;
    @include tablet {
      margin: 40px 0px;
    }
    @include desktop {
      font-weight: 450;
      font-size: rem(24);
      line-height: 32px;
      margin: 0;
      margin-bottom: 50px;
    }
  }
  &__wrapper {
    display: flex;
    justify-content: center;
  }
}
</style>
